import clsx from "clsx";
import React from "react";
import { Menu, MenuProps, MenuItem, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useDeleteLibraryExercise } from "./hooks/useDeleteLibraryExercise";
import useArchiveRestoreLibraryExerciseMutation from "./hooks/useArchiveRestoreLibraryExercise";
import {
  BodypartType,
  ExerciseAssetStatus,
  NotificationExerciseBriefDto,
} from "@growth-machine-llc/stridist-api-client";
import { useDirectApprovePendingExerciseLibraryAsset } from "./hooks/useDirectApprovePendingExerciseLibraryAsset";
import { useValidateBodyparts } from "./hooks/useValidateBodypart";

const useStyles = makeStyles(() => ({
  root: {},
}));

export interface ExercisesMenuProps
  extends Omit<MenuProps, "open" | "onClose"> {
  exerciseId: number;
  archived: boolean;
  pendingApproval?: boolean;
  exerciseNotification?: NotificationExerciseBriefDto;
  bodyparts: BodypartType[];
  onExerciseSelected: () => void;
}

export function ExercisesMenu(props: ExercisesMenuProps) {
  const {
    className,
    exerciseId,
    archived,
    pendingApproval,
    exerciseNotification,
    onExerciseSelected,
    bodyparts,
    ...other
  } = props;

  const validateBodyparts = useValidateBodyparts();

  const { mutate: deleteExercise } = useDeleteLibraryExercise(
    pendingApproval,
    exerciseNotification,
  );
  const { mutate: restore } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.ARCHIVED,
    bodyparts,
  );
  const { mutate: archive } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.PUBLISHED,
  );

  const { mutate: approve } =
    useDirectApprovePendingExerciseLibraryAsset(exerciseNotification);

  const s = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [confirmExerciseDeleteDialogOpen, setConfirmExerciseDeleteDialogOpen] =
    React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleExerciseDelete = React.useCallback(() => {
    setAnchorEl(null);
    setConfirmExerciseDeleteDialogOpen(true);
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleExerciseArchive = React.useCallback(() => {
    setAnchorEl(null);
    archive({ id: exerciseId });
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleConfirmExerciseDelete = React.useCallback(() => {
    deleteExercise(exerciseId);
    setConfirmExerciseDeleteDialogOpen(false);
  }, [exerciseId, deleteExercise]);

  const handleCancelExerciseDelete = React.useCallback(() => {
    setConfirmExerciseDeleteDialogOpen(false);
  }, []);

  const handleExerciseRestore = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    restore({ id: exerciseId });
  }, [exerciseId, restore, validateBodyparts, bodyparts]);

  const handleExerciseApprove = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    approve({ id: exerciseId });
  }, [exerciseId, approve, validateBodyparts, bodyparts]);

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <Menu
        id={exerciseId.toString()}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        className={clsx(s.root, className)}
        {...other}
      >
        {archived ? (
          <>
            <MenuItem onClick={handleExerciseRestore}>Restore</MenuItem>
            <MenuItem onClick={handleExerciseDelete}>Delete</MenuItem>
          </>
        ) : pendingApproval ? (
          <>
            <MenuItem onClick={handleExerciseApprove}>Approve</MenuItem>
            <MenuItem onClick={handleExerciseDelete}>Delete</MenuItem>
          </>
        ) : (
          <MenuItem onClick={handleExerciseArchive}>Archive</MenuItem>
        )}
      </Menu>

      {/* TODO_EXERCISE_LIBRARY: Add informative description*/}
      <ConfirmActionDialog
        title={"Are you sure you want to delete exercise?"}
        open={confirmExerciseDeleteDialogOpen}
        onClose={handleCancelExerciseDelete}
        onCancel={handleCancelExerciseDelete}
        onConfirm={handleConfirmExerciseDelete}
      />
    </>
  );
}
